import { useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { BottomSheet } from 'src/shared/components/ui/bottom-sheet'
import { Button } from 'src/shared/components/ui/buttons/button'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import ExternalLinkAlert from 'src/shared/components/ui/popups/external-link/external-link-alert'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { professionMap } from '../../schemas/profession'
import type { PublicUserData } from '../../schemas/user'
import CoverPhoto, { CoverPhotoFallback } from '../profile-photos/cover-photo'
import ProfileAvatar from '../profile-photos/profile-avatar'
import BioModal from '../public-profile/bio/bio-modal'
import OtherInfoContainer from '../public-profile/other-info-container'
import ProfileStartJourneyBtn from '../start-journey-btn/profile-start-journey-btn'

interface CustomSendInquiryButtonProps {
  externalLink: string
  title: string
  className?: string
}

function CustomSendInquiryButton({ externalLink, title, className }: CustomSendInquiryButtonProps) {
  const [isExternalPopupOpen, setIsExternalPopupOpen] = useState(false)

  const handleClick = () => {
    setIsExternalPopupOpen(true)
  }

  return (
    <>
      <Button
        variant="default"
        className={cn(
          'h-[42px] w-[180px] rounded-full bg-primary text-[14px] font-semibold text-white md:h-[50px] md:w-[200px] md:text-[15px] ',
          className,
        )}
        size={'sm'}
        onClick={handleClick}
      >
        {title}
      </Button>
      <ExternalLinkAlert isOpen={isExternalPopupOpen} setOpen={setIsExternalPopupOpen} externalLink={externalLink} />
    </>
  )
}

interface UserProfileCardProps {
  user: PublicUserData
  isPublic?: boolean
  showStartJourneyButton?: boolean
  startJourneyButtonInViewOnChange?: (inView: boolean, entry: IntersectionObserverEntry) => void
  showCustomButton?: boolean
}

export default function UserProfileCard({
  user,
  isPublic = false,
  startJourneyButtonInViewOnChange,
  showStartJourneyButton = false,
  showCustomButton = false,
}: UserProfileCardProps) {
  const [bioModalOpen, setBioModalOpen] = useState(false)

  const { isSmTabletOrSmaller } = useBreakpoint()
  const { width } = useWindowSize()
  const { ref: startJourneyButtonRef } = useInView({
    threshold: 0,
    onChange: startJourneyButtonInViewOnChange,
  })

  const { data: reviewData } = useUserReviews(user?.id || -1)
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0

  const isVerified = user.verified || false
  const professionText = professionMap[user.profession].label

  // Used to show a custom send inquiry button
  const customButtonData = useMemo(() => {
    const customLink = user.customLinks?.[0]
    if (customLink) {
      return { customButtonTitle: customLink.title, customButtonLink: customLink.url }
    }
    return undefined
  }, [user])

  if (isSmTabletOrSmaller) {
    return (
      <>
        <div className="flex w-full flex-col items-start gap-4 bg-white">
          <img
            src={user.coverPhoto || CoverPhotoFallback}
            alt="cover photo"
            className="h-[160px] w-full object-cover"
          />
          <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col rounded-[20px] bg-white sm:rounded-[40px]">
            <div className="mb-[-50px] flex translate-y-[-50px] flex-col gap-2">
              <div className="flex flex-col items-center px-5 lg:px-8">
                <ProfileAvatar
                  userId={user.id}
                  size={108}
                  className="border-4 border-white shadow-[0_1px_12px_1px_rgba(27,48,41,0.10)]"
                  shouldZoom={true}
                />
                <div className="my-[12px] flex flex-col items-center">
                  <h1 className={'mb-1 text-center text-[24px] font-semibold leading-[100%]'}>
                    <span className="mr-1">{user.fullName}</span>
                    <VerificationBadge isVerified={user.verified || false} className="inline-block" size={20} />
                  </h1>
                  <div className="flex items-center gap-2" id="mobile-public-profile-profession">
                    <p className="text-[15px] text-muted">{professionMap[user.profession].label}</p>
                  </div>
                </div>
                {!user.biography ? (
                  <div className="relative w-full items-center text-center text-[13px] italic text-muted">
                    <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                  </div>
                ) : (
                  <TruncatedText
                    text={user.biography || ''}
                    numberOfLines={2}
                    customOverflowComponent={
                      <button
                        className="inline pl-1 text-primary hover:underline"
                        onClick={() => setBioModalOpen(true)}
                      >
                        Read full bio
                      </button>
                    }
                    className="relative w-full items-center text-center text-[13px] leading-[150%] text-muted"
                  />
                )}
                <OtherInfoContainer
                  isCurrentUser={false}
                  reviewsCount={reviewsCount}
                  averageRating={averageRating}
                  shortAddress={user.shortAddress || ''}
                  className="mt-[12px] block text-center text-[12px]"
                  starSize={12}
                />
                {showCustomButton && customButtonData && (
                  <CustomSendInquiryButton
                    externalLink={customButtonData.customButtonLink}
                    title={customButtonData.customButtonTitle}
                    className="mt-4"
                  />
                )}

                {/* Start Journey Button On small tablet and smaller */}
                {showStartJourneyButton && (
                  <div className="mt-5" ref={startJourneyButtonRef}>
                    <ProfileStartJourneyBtn className="w-full" username={user.username} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <BottomSheet open={bioModalOpen} close={() => setBioModalOpen(false)}>
          <div className="flex flex-col gap-4">
            <h1 className="text-[26px] font-bold">About {user.firstName}</h1>
            <p className="text-[15px] text-muted md:text-base">{user.biography}</p>
          </div>
        </BottomSheet>
      </>
    )
  }

  return (
    <div className={cn('pointer-events-none relative z-20')}>
      <CoverPhoto
        userId={user.id}
        className={cn('h-[284px] w-full rounded-b-[30px] border-b', isPublic && 'rounded-[30px]')}
      />
      <div className="pointer-events-auto mb-[-150px] flex translate-y-[-150px] justify-center px-[20px] lg:mb-[-174px] lg:translate-y-[-174px]">
        <div className="flex h-[180px] w-full max-w-[1000px] flex-row items-center justify-between gap-6 rounded-[30px] bg-white pl-[20px] pr-[40px] text-primary shadow-[0_2px_20px_0_rgba(27,48,41,0.12)] lg:h-[225px] lg:gap-10 lg:pl-[40px] lg:pr-[60px] xl:gap-[78px]">
          <div className="flex flex-row items-center gap-2">
            <ProfileAvatar
              userId={user.id}
              size={width < 1024 ? 120 : 150}
              className="self-center border-[5px] border-white shadow-[0_1px_12px_1px_rgba(27,48,41,0.10)]"
              shouldZoom={true}
            />

            <div className="ml-[15px] flex min-w-[240px] flex-col lg:ml-[30px]">
              <h2 className={'mb-[6px] line-clamp-3 text-[24px] font-bold leading-[100%] lg:text-[30px]'}>
                <span className="mr-[5px]">{user.fullName}</span>
                <VerificationBadge isVerified={isVerified} className="mb-0.5 inline-block" size={24} />
              </h2>
              <p className="text-[14px] text-muted lg:text-[16px]">{professionText}</p>

              {(showStartJourneyButton || (customButtonData && showCustomButton)) && (
                <div className="mt-[18px] flex flex-row gap-[10px]">
                  {showCustomButton && customButtonData && (
                    <CustomSendInquiryButton
                      externalLink={customButtonData.customButtonLink}
                      title={customButtonData.customButtonTitle}
                    />
                  )}

                  {/* Start Journey Button On desktop */}
                  {showStartJourneyButton && (
                    <div ref={startJourneyButtonRef} className="w-full max-w-[240px]">
                      <ProfileStartJourneyBtn className="w-full" username={user.username} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3">
            <OtherInfoContainer
              isCurrentUser={false}
              reviewsCount={reviewsCount}
              averageRating={averageRating}
              shortAddress={user.shortAddress || ''}
              className="block lg:text-[16px]"
            />
            {!user.biography ? (
              <div className="relative w-full text-left text-sm italic text-muted lg:text-[17px]">
                <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
              </div>
            ) : (
              <TruncatedText
                key={'desktop-bio'}
                text={user.biography || ''}
                numberOfLines={3}
                customOverflowComponent={
                  <button className="inline bg-white pl-1 text-primary underline" onClick={() => setBioModalOpen(true)}>
                    More
                  </button>
                }
                className="relative w-full min-w-[120px] text-left leading-[150%] md:text-[14px] lg:text-[15px]"
              />
            )}
          </div>
        </div>
      </div>
      <BioModal isOpen={bioModalOpen} setOpen={setBioModalOpen} user={user} />
    </div>
  )
}
